/**
 * IATA airport code → IANA timezone.
 *
 * ## Why this exists
 *
 * `flight_data` stores `departing_at` as a bare `"2026-08-23 16:00"` with no
 * offset and no timezone anywhere on the segment — it is the AIRLINE'S LOCAL
 * time at the origin airport. Parsing that string in Node interprets it as the
 * *server's* local time, which on Vercel is UTC. For a St. Lucia departure
 * that is four hours wrong.
 *
 * That mattered exactly once and badly: thread f7dc7d7b, a customer stranded
 * overseas after her UVF→GND return was cancelled. The desk needs to know a
 * leg has departed and roughly how long ago, because that is what separates
 * "this trip is history" from "she is in an airport right now".
 *
 * ## Coverage and the fallback
 *
 * Every airport in booking history (187 at time of writing) plus the busiest
 * hubs we don't yet serve. Same shape and same philosophy as
 * `server/airport-metros.js`, which covers its own domain the same way.
 *
 * **An unknown code returns null, and callers must treat that as "I don't
 * know", never as UTC.** A wrong timezone silently produces a confident,
 * wrong hour count — worse than admitting the gap, because the whole point is
 * deciding whether someone is stranded right now.
 */

/** IATA → IANA. Grouped geographically so gaps are easy to spot. */
const AIRPORT_TIMEZONES = {
  // --- US Eastern
  ATL: 'America/New_York', JFK: 'America/New_York', LGA: 'America/New_York',
  EWR: 'America/New_York', BOS: 'America/New_York', PHL: 'America/New_York',
  DCA: 'America/New_York', IAD: 'America/New_York', BWI: 'America/New_York',
  MCO: 'America/New_York', MIA: 'America/New_York', FLL: 'America/New_York',
  TPA: 'America/New_York', PBI: 'America/New_York', RSW: 'America/New_York',
  JAX: 'America/New_York', SFB: 'America/New_York', PIE: 'America/New_York',
  ECP: 'America/Chicago', CLT: 'America/New_York', RDU: 'America/New_York',
  GSO: 'America/New_York', ILM: 'America/New_York', AVL: 'America/New_York',
  CHS: 'America/New_York', MYR: 'America/New_York', SAV: 'America/New_York',
  RIC: 'America/New_York', ORF: 'America/New_York', PIT: 'America/New_York',
  CLE: 'America/New_York', CAK: 'America/New_York', CMH: 'America/New_York',
  DAY: 'America/New_York', CVG: 'America/New_York', SDF: 'America/New_York',
  IND: 'America/Indiana/Indianapolis', DTW: 'America/New_York',
  GRR: 'America/New_York', BDL: 'America/New_York', PWM: 'America/New_York',
  ROC: 'America/New_York', SYR: 'America/New_York', BUF: 'America/New_York',
  // --- US Central
  ORD: 'America/Chicago', MDW: 'America/Chicago', DFW: 'America/Chicago',
  DAL: 'America/Chicago', IAH: 'America/Chicago', HOU: 'America/Chicago',
  AUS: 'America/Chicago', SAT: 'America/Chicago', MSY: 'America/Chicago',
  BNA: 'America/Chicago', MEM: 'America/Chicago', STL: 'America/Chicago',
  MCI: 'America/Chicago', MSP: 'America/Chicago', OMA: 'America/Chicago',
  DSM: 'America/Chicago', ICT: 'America/Chicago', OKC: 'America/Chicago',
  TUL: 'America/Chicago', LIT: 'America/Chicago', SHV: 'America/Chicago',
  BTR: 'America/Chicago', MLU: 'America/Chicago', MEI: 'America/Chicago',
  BHM: 'America/Chicago', PIA: 'America/Chicago', LBB: 'America/Chicago',
  ELP: 'America/Denver', ABQ: 'America/Denver',
  // --- US Mountain
  DEN: 'America/Denver', SLC: 'America/Denver', COS: 'America/Denver',
  JAC: 'America/Denver', DRO: 'America/Denver', PHX: 'America/Phoenix',
  TUS: 'America/Phoenix',
  // --- US Pacific / non-contiguous
  LAX: 'America/Los_Angeles', SFO: 'America/Los_Angeles', OAK: 'America/Los_Angeles',
  SJC: 'America/Los_Angeles', SAN: 'America/Los_Angeles', SNA: 'America/Los_Angeles',
  ONT: 'America/Los_Angeles', BUR: 'America/Los_Angeles', LGB: 'America/Los_Angeles',
  SEA: 'America/Los_Angeles', PDX: 'America/Los_Angeles', SMF: 'America/Los_Angeles',
  RNO: 'America/Los_Angeles', LAS: 'America/Los_Angeles', BOI: 'America/Boise',
  ANC: 'America/Anchorage', FAI: 'America/Anchorage',
  HNL: 'Pacific/Honolulu', OGG: 'Pacific/Honolulu', LIH: 'Pacific/Honolulu',
  KOA: 'Pacific/Honolulu',
  // --- Canada
  YYZ: 'America/Toronto', YUL: 'America/Toronto', YVR: 'America/Vancouver',
  YYC: 'America/Edmonton', YLW: 'America/Vancouver', YOW: 'America/Toronto',
  // --- Mexico, Central America, Caribbean
  CUN: 'America/Cancun', TQO: 'America/Cancun', MEX: 'America/Mexico_City', GDL: 'America/Mexico_City',
  SJD: 'America/Mazatlan', PVR: 'America/Mexico_City',
  GUA: 'America/Guatemala', BZE: 'America/Belize', LIR: 'America/Costa_Rica',
  SJO: 'America/Costa_Rica', PTY: 'America/Panama',
  SJU: 'America/Puerto_Rico', STT: 'America/St_Thomas', STI: 'America/Santo_Domingo',
  SDQ: 'America/Santo_Domingo', PUJ: 'America/Santo_Domingo',
  MBJ: 'America/Jamaica', KIN: 'America/Jamaica', NAS: 'America/Nassau',
  PLS: 'America/Grand_Turk', AUA: 'America/Aruba', CUR: 'America/Curacao',
  SXM: 'America/Lower_Princes', BGI: 'America/Barbados', POS: 'America/Port_of_Spain',
  GND: 'America/Grenada', UVF: 'America/St_Lucia', TAB: 'America/Port_of_Spain',
  // --- South America
  BOG: 'America/Bogota', CTG: 'America/Bogota', MDE: 'America/Bogota',
  LIM: 'America/Lima', SCL: 'America/Santiago', GRU: 'America/Sao_Paulo',
  GIG: 'America/Sao_Paulo', CCS: 'America/Caracas', EZE: 'America/Argentina/Buenos_Aires',
  // --- Western Europe
  LHR: 'Europe/London', LGW: 'Europe/London', EDI: 'Europe/London',
  MAN: 'Europe/London', DUB: 'Europe/Dublin',
  CDG: 'Europe/Paris', ORY: 'Europe/Paris', NCE: 'Europe/Paris', TLS: 'Europe/Paris',
  AMS: 'Europe/Amsterdam', BRU: 'Europe/Brussels', FRA: 'Europe/Berlin',
  MUC: 'Europe/Berlin', ZRH: 'Europe/Zurich', VIE: 'Europe/Vienna',
  MAD: 'Europe/Madrid', BCN: 'Europe/Madrid', SVQ: 'Europe/Madrid',
  LIS: 'Europe/Lisbon', FCO: 'Europe/Rome', MXP: 'Europe/Rome',
  MLA: 'Europe/Malta', ATH: 'Europe/Athens', JMK: 'Europe/Athens',
  CPH: 'Europe/Copenhagen', ARN: 'Europe/Stockholm', OSL: 'Europe/Oslo',
  BGO: 'Europe/Oslo', KEF: 'Atlantic/Reykjavik', DBV: 'Europe/Zagreb',
  OTP: 'Europe/Bucharest', TIA: 'Europe/Tirane', IST: 'Europe/Istanbul',
  // --- Africa & Middle East
  RAK: 'Africa/Casablanca', CAI: 'Africa/Cairo', LOS: 'Africa/Lagos',
  ABV: 'Africa/Lagos', ACC: 'Africa/Accra', ADD: 'Africa/Addis_Ababa',
  EBB: 'Africa/Kampala', JRO: 'Africa/Dar_es_Salaam', ZNZ: 'Africa/Dar_es_Salaam',
  CPT: 'Africa/Johannesburg', JNB: 'Africa/Johannesburg',
  DXB: 'Asia/Dubai', AUH: 'Asia/Dubai', DOH: 'Asia/Qatar', TLV: 'Asia/Jerusalem',
  // --- Asia & Pacific
  NRT: 'Asia/Tokyo', HND: 'Asia/Tokyo', ICN: 'Asia/Seoul', TPE: 'Asia/Taipei',
  TSA: 'Asia/Taipei', HKG: 'Asia/Hong_Kong', SIN: 'Asia/Singapore',
  BKK: 'Asia/Bangkok', HKT: 'Asia/Bangkok', HAN: 'Asia/Ho_Chi_Minh',
  MNL: 'Asia/Manila', MPH: 'Asia/Manila', TAG: 'Asia/Manila', ENI: 'Asia/Manila',
  DPS: 'Asia/Makassar', KUL: 'Asia/Kuala_Lumpur', DEL: 'Asia/Kolkata',
  BOM: 'Asia/Kolkata', SYD: 'Australia/Sydney', MEL: 'Australia/Melbourne',
  MQL: 'Australia/Melbourne', AKL: 'Pacific/Auckland',
  // --- Search demand (abandoned_trips) not yet in booking history ---
  // US Eastern
  ACY: 'America/New_York', AGS: 'America/New_York', APN: 'America/New_York',
  CAE: 'America/New_York', CHO: 'America/New_York', CRW: 'America/New_York',
  MBS: 'America/New_York', MDT: 'America/New_York', MHT: 'America/New_York',
  ROA: 'America/New_York', SRQ: 'America/New_York', TYS: 'America/New_York',
  // US Central
  AEX: 'America/Chicago', CRP: 'America/Chicago', FAR: 'America/Chicago',
  HRL: 'America/Chicago', HSV: 'America/Chicago', LBF: 'America/Chicago',
  LNK: 'America/Chicago', MAF: 'America/Chicago', MKE: 'America/Chicago',
  VPS: 'America/Chicago', XNA: 'America/Chicago',
  // US Mountain / Pacific
  BIL: 'America/Denver', BTM: 'America/Denver', BZN: 'America/Denver',
  GJT: 'America/Denver', SCF: 'America/Phoenix',
  FAT: 'America/Los_Angeles', GEG: 'America/Los_Angeles', MFR: 'America/Los_Angeles',
  // Canada
  YFC: 'America/Moncton', YQM: 'America/Moncton', YHZ: 'America/Halifax',
  YTZ: 'America/Toronto',
  // Caribbean / Latin America
  ANU: 'America/Antigua', BQN: 'America/Puerto_Rico', STX: 'America/St_Thomas',
  FPO: 'America/Nassau', POP: 'America/Santo_Domingo', HAV: 'America/Havana',
  SAL: 'America/El_Salvador', BSB: 'America/Sao_Paulo',
  DGO: 'America/Mexico_City', MTT: 'America/Mexico_City', MZT: 'America/Mazatlan',
  // Europe
  AGP: 'Europe/Madrid', BUD: 'Europe/Budapest', WAW: 'Europe/Warsaw',
  HAM: 'Europe/Berlin', MRS: 'Europe/Paris', NCL: 'Europe/London',
  FLR: 'Europe/Rome', GOA: 'Europe/Rome', LIN: 'Europe/Rome', PSA: 'Europe/Rome',
  SKP: 'Europe/Skopje', PDL: 'Atlantic/Azores',
  // Africa & Middle East
  ABJ: 'Africa/Abidjan', ATR: 'Africa/Nouakchott', BJL: 'Africa/Banjul',
  CMN: 'Africa/Casablanca', DSS: 'Africa/Dakar', KGL: 'Africa/Kigali',
  NBE: 'Africa/Tunis', NBO: 'Africa/Nairobi',
  JED: 'Asia/Riyadh', RUH: 'Asia/Riyadh', ISB: 'Asia/Karachi',
  // Asia & Pacific
  AXT: 'Asia/Tokyo', KIX: 'Asia/Tokyo', OKA: 'Asia/Tokyo', OKD: 'Asia/Tokyo',
  CEB: 'Asia/Manila', DAD: 'Asia/Ho_Chi_Minh', SGN: 'Asia/Ho_Chi_Minh',
  MLE: 'Indian/Maldives', PPT: 'Pacific/Tahiti',
};

/**
 * IANA timezone for an airport, or `null` when we don't have it.
 *
 * Null is a real answer. Callers must degrade honestly rather than assume UTC
 * — see the module header.
 */
export function timezoneForAirport(code) {
  const key = String(code || '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(key)) return null;
  return AIRPORT_TIMEZONES[key] || null;
}

/** Airports we can resolve — used by the coverage test. */
export function knownAirportCodes() {
  return Object.keys(AIRPORT_TIMEZONES);
}

/**
 * Turn a timezone-naive airline-local stamp into a real instant.
 *
 * `"2026-08-23 16:00"` at UVF means 16:00 in `America/St_Lucia`. We find the
 * UTC instant whose local rendering in that zone matches the input, by
 * measuring the zone's offset at an approximate instant and correcting. Two
 * passes settle DST boundaries, where the offset at the guess differs from the
 * offset at the answer.
 *
 * Returns epoch ms, or `null` if the stamp or the zone is unusable — so a
 * caller can tell "no answer" apart from a wrong one.
 */
export function parseAirlineLocalTime(stamp, timeZone) {
  const raw = String(stamp || '').trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number);

  // If the stamp already carries an offset or Z, it is absolute — trust it.
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(raw)) {
    const abs = Date.parse(raw);
    return Number.isFinite(abs) ? abs : null;
  }
  if (!timeZone) return null;

  // Treat the wall-clock as if it were UTC, then subtract the zone's offset.
  const asUtc = Date.UTC(y, mo - 1, d, h, mi);
  let guess = asUtc;
  for (let i = 0; i < 2; i += 1) {
    const offset = zoneOffsetMs(guess, timeZone);
    if (offset === null) return null;
    guess = asUtc - offset;
  }
  return guess;
}

/** How far ahead of UTC `timeZone` is at `instantMs`, in ms. */
function zoneOffsetMs(instantMs, timeZone) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const p = Object.fromEntries(dtf.formatToParts(new Date(instantMs)).map((x) => [x.type, x.value]));
    // Intl renders hour 24 for midnight in some environments.
    const hour = Number(p.hour) % 24;
    const asIfUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), Number(p.second));
    return asIfUtc - instantMs;
  } catch {
    return null;
  }
}
